import React, { useState } from 'react';
import { CreditCard, Calendar, TrendingUp, Edit, Save, AlertCircle } from 'lucide-react';
import CustomDatePicker from '../shared/CustomDatePicker';
import { getPlanById, SUBSCRIPTION_PLANS } from '../../../../config/subscriptionPlans';
import { companyService } from '../../../../services/companyService';

const CompanySubscriptionTab = ({ company, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        subscriptionPlan: company.subscriptionPlan,
        subscriptionExpiryDate: company.subscriptionExpiryDate,
        status: company.status
    });
    const [saving, setSaving] = useState(false);

    const currentPlan = getPlanById(company.subscriptionPlan);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await companyService.updateCompany(company.id, { ...company, ...formData });
            setIsEditing(false);
            if (onUpdate) {
                await onUpdate();
            }
            alert('Subscription updated successfully!');
        } catch (error) {
            console.error('Failed to update subscription:', error);
            alert('Failed to update subscription');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            subscriptionPlan: company.subscriptionPlan,
            subscriptionExpiryDate: company.subscriptionExpiryDate,
            status: company.status
        });
        setIsEditing(false);
    };

    const handleActivate = async () => {
        if (window.confirm('Are you sure you want to activate this subscription?')) {
            try {
                setSaving(true);
                await companyService.updateCompany(company.id, { ...company, status: 'active' });
                if (onUpdate) {
                    await onUpdate();
                }
                alert('Subscription activated successfully!');
            } catch (error) {
                console.error('Failed to activate subscription:', error);
                alert('Failed to activate subscription');
            } finally {
                setSaving(false);
            }
        }
    };

    const handleDeactivate = async () => {
        if (window.confirm('Are you sure you want to deactivate this subscription? The company will lose access to the system.')) {
            try {
                setSaving(true);
                await companyService.updateCompany(company.id, { ...company, status: 'suspended' });
                if (onUpdate) {
                    await onUpdate();
                }
                alert('Subscription deactivated successfully!');
            } catch (error) {
                console.error('Failed to deactivate subscription:', error);
                alert('Failed to deactivate subscription');
            } finally {
                setSaving(false);
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime()) || date.getFullYear() === 1970) return 'N/A';
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    };

    const getDaysUntilExpiry = () => {
        if (!company.subscriptionExpiryDate) return 999; // Assume indefinite if not set
        const expiry = new Date(company.subscriptionExpiryDate);
        if (isNaN(expiry.getTime()) || expiry.getFullYear() === 1970) return 999;
        const today = new Date();
        const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
        return days;
    };

    const daysLeft = getDaysUntilExpiry();

    return (
        <div className="p-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        Subscription Management
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">Manage subscription plan and billing</p>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
                    >
                        <Edit size={16} />
                        Edit
                    </button>
                )}
            </div>

            {/* Expiry Warning */}
            {company.subscriptionExpiryDate && daysLeft <= 30 && daysLeft > 0 && (
                <div className="mb-6 bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle size={20} className="text-orange-600 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-orange-900 mb-1">Subscription Expiring Soon</h4>
                        <p className="text-sm text-orange-700">
                            This subscription will expire in {daysLeft} days on {formatDate(company.subscriptionExpiryDate)}.
                        </p>
                    </div>
                </div>
            )}

            {company.subscriptionExpiryDate && daysLeft <= 0 && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle size={20} className="text-red-600 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-red-900 mb-1">Subscription Expired</h4>
                        <p className="text-sm text-red-700">
                            This subscription expired {Math.abs(daysLeft)} days ago on {formatDate(company.subscriptionExpiryDate)}.
                        </p>
                    </div>
                </div>
            )}

            {/* Current Plan Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-purple-100/50 p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <CreditCard size={20} className="text-green-600" />
                    Current Plan
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Subscription Plan
                        </label>
                        {isEditing ? (
                            <select
                                name="subscriptionPlan"
                                value={formData.subscriptionPlan}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            >
                                {SUBSCRIPTION_PLANS.map(plan => (
                                    <option key={plan.id} value={plan.id}>
                                        {plan.name} - {plan.currency} {plan.price}/{plan.billingCycle}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className={`px-4 py-3 rounded-lg ${currentPlan?.color === 'blue' ? 'bg-blue-50' :
                                currentPlan?.color === 'purple' ? 'bg-purple-50' :
                                    currentPlan?.color === 'green' ? 'bg-green-50' :
                                        currentPlan?.color === 'indigo' ? 'bg-indigo-50' :
                                            'bg-gray-50'
                                }`}>
                                <div className="font-bold text-lg text-gray-900">
                                    {currentPlan?.name || (company.subscriptionPlan ? company.subscriptionPlan.charAt(0).toUpperCase() + company.subscriptionPlan.slice(1) : 'Unknown')}
                                </div>
                                {currentPlan && (
                                    <div className="text-sm text-gray-600">
                                        {currentPlan.currency} {currentPlan.price}/{currentPlan.billingCycle}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Status
                        </label>
                        {isEditing ? (
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            >
                                <option value="active">Active</option>
                                <option value="suspended">Suspended</option>
                                <option value="expired">Expired</option>
                            </select>
                        ) : (
                            <div className={`px-4 py-3 rounded-lg ${company.status === 'active' ? 'bg-green-50' :
                                company.status === 'expired' ? 'bg-red-50' :
                                    company.status === 'suspended' ? 'bg-gray-50' :
                                        'bg-orange-50'
                                }`}>
                                <div className={`font-bold text-lg ${company.status === 'active' ? 'text-green-700' :
                                    company.status === 'expired' ? 'text-red-700' :
                                        company.status === 'suspended' ? 'text-gray-700' :
                                            'text-orange-700'
                                    }`}>
                                    {company.status.charAt(0).toUpperCase() + company.status.slice(1).replace('_', ' ')}
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Start Date
                        </label>
                        <div className="px-4 py-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2 text-gray-700">
                                <Calendar size={16} />
                                <span className="font-medium">{formatDate(company.subscriptionStartDate)}</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Expiry Date
                        </label>
                        {isEditing ? (
                            <CustomDatePicker
                                mode="single"
                                value={formData.subscriptionExpiryDate}
                                onChange={(val) => handleInputChange({ target: { name: 'subscriptionExpiryDate', value: val } })}
                                quickFilters={false}
                                className="w-full"
                            />
                        ) : (
                            <div className={`px-4 py-3 rounded-lg ${daysLeft < 0 ? 'bg-red-50' :
                                daysLeft <= 30 ? 'bg-orange-50' :
                                    'bg-gray-50'
                                }`}>
                                <div className="flex items-center gap-2">
                                    <Calendar size={16} className={
                                        daysLeft < 0 ? 'text-red-600' :
                                            daysLeft <= 30 ? 'text-orange-600' :
                                                'text-gray-600'
                                    } />
                                    <span className={`font-medium ${daysLeft < 0 ? 'text-red-700' :
                                        daysLeft <= 30 ? 'text-orange-700' :
                                            'text-gray-700'
                                        }`}>
                                        {formatDate(company.subscriptionExpiryDate)}
                                    </span>
                                </div>
                                <div className={`text-xs mt-1 ${daysLeft < 0 ? 'text-red-600' :
                                    daysLeft <= 30 ? 'text-orange-600' :
                                        'text-gray-500'
                                    }`}>
                                    {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)} days ago` :
                                        daysLeft === 0 ? 'Expires today' :
                                            `${daysLeft} days remaining`}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                {isEditing && (
                    <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-gray-200">
                        <button
                            onClick={handleCancel}
                            disabled={saving}
                            className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-semibold disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Subscription Control Buttons - System Admin Only */}
            {!isEditing && (
                <div className="bg-white rounded-2xl shadow-lg border border-purple-100/50 p-6 mb-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Subscription Control</h3>
                    <p className="text-sm text-gray-600 mb-4">
                        {company.status === 'active'
                            ? 'Deactivate this subscription to suspend company access to the system.'
                            : 'Activate this subscription to restore company access to the system.'}
                    </p>
                    <div className="flex items-center gap-3">
                        {company.status !== 'active' ? (
                            <button
                                onClick={handleActivate}
                                disabled={saving}
                                className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 flex items-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Activating...
                                    </>
                                ) : (
                                    <>
                                        <CreditCard size={16} />
                                        Activate Subscription
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleDeactivate}
                                disabled={saving}
                                className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg hover:from-red-600 hover:to-rose-700 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 flex items-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Deactivating...
                                    </>
                                ) : (
                                    <>
                                        <AlertCircle size={16} />
                                        Deactivate Subscription
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Plan Features */}
            {!isEditing && currentPlan && (
                <div className="bg-white rounded-2xl shadow-lg border border-purple-100/50 p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <TrendingUp size={20} className="text-purple-600" />
                        Plan Features & Limits
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                            <h4 className="font-semibold text-gray-700 text-sm">Features</h4>
                            {currentPlan.features.map((feature, index) => (
                                <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                    {feature}
                                </div>
                            ))}
                        </div>

                        <div className="space-y-3">
                            <h4 className="font-semibold text-gray-700 text-sm">Usage Limits</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">Users</span>
                                    <span className="font-semibold text-gray-900">
                                        {company.userCount} / {currentPlan.limits.users === -1 ? '∞' : currentPlan.limits.users}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">Storage</span>
                                    <span className="font-semibold text-gray-900">
                                        {(company.storageUsed / 1024).toFixed(2)} GB / {(currentPlan.limits.storage / 1024).toFixed(0)} GB
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">Products</span>
                                    <span className="font-semibold text-gray-900">
                                        {currentPlan.limits.products === -1 ? 'Unlimited' : currentPlan.limits.products}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">Orders</span>
                                    <span className="font-semibold text-gray-900">
                                        {currentPlan.limits.orders === -1 ? 'Unlimited' : currentPlan.limits.orders}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanySubscriptionTab;
